import { useEffect, useMemo, useState } from "react";
import {
  buildResolvedOrgRolePermissions,
  PERMISSION_CATALOG,
  resolveRolePermissions,
  type OrgRolePermissions,
  type PermissionMap,
  type PermissionResource,
} from "../../permission-catalog";
import type { AppId } from "../../app-catalog";
import { DEFAULT_ENABLED_APPS } from "../../app-catalog";
import type { OrgRoleName } from "../../auth/permissions";
import type { EnabledOrgRoles } from "../../role-catalog";
import {
  filterAppPermissionActions,
  getVisiblePermissionTabs,
  humanizeActionLabel,
  isOwnerLockedPermission,
  type PermissionTabKey,
  RESOURCE_ICONS,
  RESOURCE_LABELS,
  togglePermission,
} from "./role-permission-utils";
import { Checkbox } from "../ui/checkbox";

export interface OrgRolePermissionsPanelProps {
  enabledOrgRoles: EnabledOrgRoles;
  enabledApps?: AppId[];
  rolePermissions: OrgRolePermissions;
  roleKey: OrgRoleName | null;
  onChange: (next: OrgRolePermissions) => void;
  manageAvailability?: boolean;
}

interface PermissionGroupBlockProps {
  resource: PermissionResource;
  permissions: PermissionMap;
  selectedRoleKey: OrgRoleName;
  enabledApps: AppId[];
  onToggle: (resource: string, action: string, enabled: boolean) => void;
}

function PermissionGroupBlock({
  resource,
  permissions,
  selectedRoleKey,
  enabledApps,
  onToggle,
}: PermissionGroupBlockProps) {
  const actions =
    resource === "app"
      ? filterAppPermissionActions(PERMISSION_CATALOG.app, enabledApps)
      : [...PERMISSION_CATALOG[resource]];
  const Icon = RESOURCE_ICONS[resource];

  return (
    <div className="rounded-2xl border border-border bg-card p-4">
      <div className="mb-3 flex items-center gap-3">
        {Icon ? (
          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl border border-[var(--primary-soft-border)] bg-[var(--primary-soft-bg)] dark:border-violet-300/30 dark:bg-violet-500/15">
            <Icon className="h-5 w-5 text-[#7c3aed] dark:text-violet-300" aria-hidden />
          </div>
        ) : null}
        <h3 className="text-sm font-semibold text-foreground">{RESOURCE_LABELS[resource]}</h3>
      </div>
      <ul className="space-y-1.5">
        {actions.map((action) => {
          const checked = permissions[resource]?.includes(action) ?? false;
          const locked = isOwnerLockedPermission(selectedRoleKey, resource, action);
          return (
            <li
              key={`${resource}:${action}`}
              className="flex items-center justify-between gap-3 rounded px-1 py-0.5 text-sm text-muted-foreground hover:bg-muted"
            >
              <span>{humanizeActionLabel(resource, action)}</span>
              <Checkbox
                checked={checked}
                disabled={locked}
                onCheckedChange={(value: boolean | "indeterminate") =>
                  onToggle(resource, action, value === true)
                }
              />
            </li>
          );
        })}
      </ul>
    </div>
  );
}

export function OrgRolePermissionsPanel({
  enabledOrgRoles,
  enabledApps = DEFAULT_ENABLED_APPS,
  rolePermissions,
  roleKey,
  onChange,
  manageAvailability = false,
}: OrgRolePermissionsPanelProps) {
  const visibleTabs = useMemo(() => getVisiblePermissionTabs(enabledApps), [enabledApps]);
  const [activeTab, setActiveTab] = useState<PermissionTabKey>("application");

  useEffect(() => {
    if (!visibleTabs.some((tab) => tab.key === activeTab)) {
      setActiveTab(visibleTabs[0]?.key ?? "application");
    }
  }, [activeTab, visibleTabs]);

  if (!roleKey) {
    return (
      <p className="text-sm text-muted-foreground">
        {manageAvailability
          ? "Select a membership role to configure its permissions."
          : "No membership roles are enabled for this organization. Contact your system administrator."}
      </p>
    );
  }

  if (!enabledOrgRoles[roleKey]) {
    return (
      <p className="text-sm text-muted-foreground">
        {manageAvailability
          ? "Enable this role to configure its permissions."
          : "No membership roles are enabled for this organization. Contact your system administrator."}
      </p>
    );
  }

  const selectedRoleKey = roleKey;
  const resolved = buildResolvedOrgRolePermissions(rolePermissions, enabledOrgRoles);
  const permissions =
    resolved[selectedRoleKey] ?? resolveRolePermissions(selectedRoleKey, rolePermissions);
  const activeTabDef = visibleTabs.find((tab) => tab.key === activeTab) ?? visibleTabs[0];

  function updateRolePermissions(nextMap: PermissionMap) {
    onChange({
      ...rolePermissions,
      [selectedRoleKey]: nextMap,
    });
  }

  function handleToggle(resource: string, action: string, enabled: boolean) {
    updateRolePermissions(togglePermission(permissions, resource, action, enabled));
  }

  if (!activeTabDef) {
    return (
      <p className="text-sm text-muted-foreground">
        No applications are enabled for this organization. Contact your system administrator.
      </p>
    );
  }

  return (
    <div>
      <div className="flex w-fit gap-1 rounded-lg bg-muted p-1">
        {visibleTabs.map((tab) => (
          <button
            key={tab.key}
            type="button"
            onClick={() => setActiveTab(tab.key)}
            className={`rounded-md px-4 py-2 text-sm font-medium transition-all ${
              activeTab === tab.key
                ? "bg-card text-foreground shadow-sm"
                : "text-muted-foreground hover:bg-background/80 hover:text-foreground"
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      <div className="mt-4 grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        {activeTabDef.resources.map((resource) => (
          <PermissionGroupBlock
            key={resource}
            resource={resource}
            permissions={permissions}
            selectedRoleKey={selectedRoleKey}
            enabledApps={enabledApps}
            onToggle={handleToggle}
          />
        ))}
      </div>
    </div>
  );
}
